/* eslint brace-style: ["error", "allman"] */
/**
 * User search — the one way HPC finds people by what an admin types.
 *
 * A person is matched on first name, last name, email and login ID (LADOTD login IDs are employee numbers, e.g.
 * `00266863@ladotd`). The query is split on whitespace and every word must match one of those fields, so a full
 * name ("Gayle Ritchie"), a reversed one ("Ritchie, Gayle"), a login ID or an email all find the person. It is a
 * single `Users/FilteredTo` request: each word becomes a parenthesized OR group across the fields, and the groups
 * AND together with the caller's scope (customer, Deleted) — verified on QA that the server honors the grouping.
 *
 * The filter itself is pict-section-picker's `buildSearchFilter`, a pure function, reused rather than rebuilt.
 */

const libPictSectionPicker = require('pict-section-picker');

/** User columns a person is found by (stricture DDL: lib/headlight_model/ddl/systemmanagement/Account.mddl). */
const USER_SEARCH_FIELDS = [ 'NameFirst', 'NameLast', 'Email', 'LoginID' ];

/** Results come back in name order, so a capped page is the first N people alphabetically. */
const USER_SORT = 'FSF~NameLast~ASC~0~FSF~NameFirst~ASC~0';

const DEFAULT_PAGE_SIZE = 50;
const MAX_PAGE_SIZE = 250;

/**
 * @typedef {Object} UserSearchOptions
 * @property {string | (() => string)} [Scope] - FoxHound stanzas ANDed onto the search, e.g.
 *   `FBV~IDCustomer~EQ~10~FBV~Deleted~EQ~0`; a function is read at call time (pickers outlive a customer switch).
 * @property {number} [Start] - Record offset (default 0).
 * @property {number} [PageSize] - Records per request (default 50, never above 250).
 */

/**
 * @typedef {Object} UserPickerOption
 * @property {number} Value - IDUser.
 * @property {string} Text - The person's display label.
 * @property {string} Tag - Login ID (else email), drawn as a pill so two people with the same name can be told apart.
 * @property {Record<string, any>} Record - The User record.
 */

/**
 * Tidy what the user typed into search words: `~` is FoxHound's separator, a comma separates "Last, First", and
 * `*` reads as a wildcard.
 *
 * @param {any} pQuery - The raw search text.
 * @returns {string} The cleaned query (single-spaced, trimmed; '' when nothing is left).
 */
function cleanQuery(pQuery)
{
	return String(pQuery == null ? '' : pQuery).replace(/[~,]/g, ' ').replace(/\*/g, '%').replace(/\s+/g, ' ').trim();
}

/**
 * Filter stanzas matching every word of the query against any of the fields (one OR group per word, ANDed).
 *
 * @param {Array<string>} pFields - Entity columns to LIKE-match.
 * @param {any} pQuery - The raw search text.
 * @returns {string} FoxHound filter stanzas, or '' for an empty query.
 */
function buildSearchFilter(pFields, pQuery)
{
	return libPictSectionPicker.prototype.buildSearchFilter.call(null, pFields, cleanQuery(pQuery));
}

/**
 * @param {any} pQuery - The raw search text.
 * @returns {string} FoxHound filter stanzas matching people by name, email or login ID, or '' for an empty query.
 */
function buildUserSearchFilter(pQuery)
{
	return buildSearchFilter(USER_SEARCH_FIELDS, pQuery);
}

/**
 * @param {UserSearchOptions} [pOptions]
 * @returns {number} The requested page size, clamped to 1..250.
 */
function pageSize(pOptions)
{
	const tmpSize = Math.floor(Number(pOptions && pOptions.PageSize) || DEFAULT_PAGE_SIZE);
	return Math.min(Math.max(tmpSize, 1), MAX_PAGE_SIZE);
}

/**
 * The `Users/FilteredTo` URL for a search. An empty query browses the scope, still in name order.
 *
 * @param {any} pQuery - The raw search text.
 * @param {UserSearchOptions} [pOptions]
 * @returns {string} A URL relative to the API root (`Users/FilteredTo/<filter>/<start>/<count>`).
 */
function buildUserSearchURL(pQuery, pOptions)
{
	const tmpOptions = pOptions || {};
	const fScope = tmpOptions.Scope;
	const tmpScope = (typeof fScope === 'function') ? fScope() : fScope;
	const tmpFilter = [ buildUserSearchFilter(pQuery), tmpScope, USER_SORT ].filter(Boolean).join('~');
	const tmpStart = Math.max(Math.floor(Number(tmpOptions.Start) || 0), 0);
	return `Users/FilteredTo/${tmpFilter}/${tmpStart}/${pageSize(tmpOptions)}`;
}

/**
 * Run a user search.
 *
 * @param {(pURL: string) => Promise<any>} fGetJSON - The caller's API GET (relative URL → parsed body).
 * @param {any} pQuery - The raw search text.
 * @param {UserSearchOptions} [pOptions]
 * @returns {Promise<Array<Record<string, any>>>} Matching User records in name order ([] for an error body).
 */
async function searchUsersAsync(fGetJSON, pQuery, pOptions)
{
	const tmpRows = await fGetJSON(buildUserSearchURL(pQuery, pOptions));
	return Array.isArray(tmpRows) ? tmpRows : [];
}

/**
 * How a person is shown: their name, else their login ID, else their email.
 *
 * @param {Record<string, any>} pUser - A User record.
 * @returns {string} The display label.
 */
function userLabel(pUser)
{
	if (!pUser)
	{
		return '';
	}
	const tmpName = `${pUser.NameFirst || ''} ${pUser.NameLast || ''}`.trim();
	return tmpName || pUser.LoginID || pUser.Email || `User #${pUser.IDUser}`;
}

/**
 * The picker option for a person: their label, tagged with their login ID (else email) unless that is already the label.
 *
 * @param {Record<string, any>} pUser - A User record.
 * @returns {UserPickerOption} The option.
 */
function userPickerOption(pUser)
{
	const tmpText = userLabel(pUser);
	const tmpTag = [ pUser.LoginID, pUser.Email ].find((pValue) => pValue && (pValue !== tmpText)) || '';
	return { Value: Number(pUser.IDUser), Text: tmpText, Tag: tmpTag, Record: pUser };
}

/**
 * A pict-section-picker DataProvider over the user search: `(term, page) => { results, hasMore }`. An empty term
 * browses the scope page by page; a failed request shows an empty list.
 *
 * @param {(pURL: string) => Promise<any>} fGetJSON - The caller's API GET.
 * @param {UserSearchOptions & { ToOption?: (pUser: Record<string, any>) => UserPickerOption }} [pOptions]
 * @returns {(pTerm: any, pPage: number) => Promise<{ results: Array<UserPickerOption>, hasMore: boolean }>}
 */
function userPickerDataProvider(fGetJSON, pOptions)
{
	const tmpOptions = pOptions || {};
	const tmpPageSize = pageSize(tmpOptions);
	const fToOption = tmpOptions.ToOption || userPickerOption;
	return (pTerm, pPage) =>
	{
		const tmpSearch = Object.assign({}, tmpOptions, { Start: (Number(pPage) || 0) * tmpPageSize, PageSize: tmpPageSize });
		return searchUsersAsync(fGetJSON, pTerm, tmpSearch)
			.then((pRows) => ({ results: pRows.map(fToOption), hasMore: (pRows.length >= tmpPageSize) }))
			.catch(() => ({ results: [], hasMore: false }));
	};
}

module.exports =
{
	USER_SEARCH_FIELDS,
	MAX_PAGE_SIZE,
	cleanQuery,
	buildSearchFilter,
	buildUserSearchFilter,
	buildUserSearchURL,
	searchUsersAsync,
	userLabel,
	userPickerOption,
	userPickerDataProvider,
};
